'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useAuthStore } from '@/store/authStore';
import { useChatStore } from '@/store/chatStore';
import { useUIStore } from '@/store/uiStore';
import { api } from '@/lib/api';

export function useAuth() {
  const router = useRouter();
  const { user, token, isAuthenticated, isPremium, isAdmin, setAuth, clearAuth } = useAuthStore();
  const { addToast } = useUIStore();

  const login = useCallback(
    async (email: string, password: string) => {
      const data = (await api.auth.login({ email, password })) as { token: string; user: Parameters<typeof setAuth>[0] };
      setAuth(data.user, data.token);
      if (typeof data.user.lifetimeConnections === 'number') {
        useChatStore.getState().setLifetimeConnections(data.user.lifetimeConnections);
      }
      addToast({ message: 'Welcome back!', type: 'success' });
      return data;
    },
    [setAuth, addToast]
  );

  const register = useCallback(
    async (email: string, password: string, username?: string) => {
      const data = (await api.auth.register({ email, password, username })) as { token: string; user: Parameters<typeof setAuth>[0] };
      setAuth(data.user, data.token);
      if (typeof data.user.lifetimeConnections === 'number') {
        useChatStore.getState().setLifetimeConnections(data.user.lifetimeConnections);
      }
      addToast({ message: 'Account created!', type: 'success' });
      return data;
    },
    [setAuth, addToast]
  );

  const logout = useCallback(async () => {
    if (token) {
      await api.auth.logout(token).catch(() => {});
    }
    clearAuth();
    router.push('/');
    addToast({ message: 'Logged out', type: 'info' });
  }, [token, clearAuth, router, addToast]);

  const redirectToCheckout = useCallback(async () => {
    if (!isAuthenticated) {
      router.push('/login?redirect=/subscribe');
      return;
    }
    const data = (await api.subscriptions.createCheckout(token!)) as { url: string };
    window.location.href = data.url;
  }, [isAuthenticated, token, router]);

  return {
    user,
    token,
    isAuthenticated,
    isPremium,
    isAdmin,
    login,
    register,
    logout,
    redirectToCheckout,
  };
}
